/**
 * Hook for fetching and saving Care Queue resident tab configuration.
 *
 * Uses React Query for caching and the unified API service for requests.
 * Supports both main app and widget contexts via optional `api` prop.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiService, type ApiService } from "../services/apiService";
import type { ConfigurationState } from "../types/configuration";
import {
  mapApiResponseToConfig,
  mapConfigToApiRequest,
} from "../types/configuration";

const QUERY_KEY = ["care-queue-resident-tab-config"];

interface UseResidentTabConfigurationOptions {
  /** Only fetch when true (e.g., when modal is open) */
  enabled?: boolean;
  /** Custom API service instance (for widget usage with different auth) */
  api?: ApiService;
}

interface UseResidentTabConfigurationResult {
  /** The resolved configuration (camelCase frontend shape) */
  config: ConfigurationState | undefined;
  /** True during initial fetch */
  isLoading: boolean;
  /** Error if fetch failed */
  error: Error | null;
  /** Save configuration to the backend */
  saveConfig: (config: ConfigurationState) => Promise<void>;
  /** True while save is in progress */
  isSaving: boolean;
  /** Error if save failed */
  saveError: Error | null;
}

export function useResidentTabConfiguration({
  enabled = false,
  api,
}: UseResidentTabConfigurationOptions = {}): UseResidentTabConfigurationResult {
  const queryClient = useQueryClient();
  const service = api ?? apiService;

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async ({ signal }) => {
      const [configResponse, riskConfigResponse] = await Promise.all([
        service.getResidentTabConfiguration(signal),
        service.getRiskFactorConfig(signal),
      ]);
      const config = mapApiResponseToConfig(configResponse.config);
      // Merge disabled risk factors from the separate risk config API.
      // Use the first facility's config as a representative value for the modal.
      const firstEntry = riskConfigResponse
        ? Object.values(riskConfigResponse)[0]
        : null;
      config.disabledRiskFactors =
        firstEntry?.custom_config?.disabled_risk_factors ?? [];
      return config;
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const mutation = useMutation({
    mutationFn: async (config: ConfigurationState) => {
      const payload = mapConfigToApiRequest(config);
      await service.saveResidentTabConfiguration(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  return {
    config: query.data,
    isLoading: query.isLoading,
    error: query.error,
    saveConfig: async (config: ConfigurationState) => {
      await mutation.mutateAsync(config);
    },
    isSaving: mutation.isPending,
    saveError: mutation.error,
  };
}
